import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Send, Mail, Globe, Loader2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const inputCls = "w-full border-b border-[#27272A] bg-transparent py-4 text-base text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white rounded-none";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", interest: "General Enquiry", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">Contact</p>
          <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            Let's talk<span className="text-brand">.</span>
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
            Course questions, custom batch requests, a textbook nobody else will teach —
            send it over. Every enquiry lands directly with the mentor.
          </p>
          <div className="mt-12 flex flex-col gap-6 text-sm text-zinc-400">
            <div className="flex items-center gap-4">
              <Mail size={18} className="text-brand" />
              <span data-testid="contact-email-label">hello@ppacedemy.in</span>
            </div>
            <div className="flex items-center gap-4">
              <Globe size={18} className="text-brand" />
              <span>ppacedemy.in — India</span>
            </div>
          </div>
        </motion.div>
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ delay: 0.15, duration: 0.8, ease: EASE }}
          className="lg:col-span-7"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
            <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={inputCls} />
            <select data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className={`${inputCls} cursor-pointer bg-[#050505]`}>
              <option>General Enquiry</option>
              <option>Engineering Courses</option>
              <option>Trading Courses</option>
              <option>School Classes 1–12</option>
              <option>MBA Finance</option>
              <option>Custom Batch / 1-on-1</option>
            </select>
          </div>
          <textarea
            data-testid="contact-message-input"
            required
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us what you want to learn..."
            rows={5}
            className={`${inputCls} mt-8 resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="mt-12 flex items-center gap-3 bg-brand px-10 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
